import React, { useEffect, useState } from 'react';
import { LandingPage } from './pages/LandingPage';
import { FeedPage } from './pages/FeedPage';
import { EventsPage } from './pages/EventsPage';
import { GoFishPage } from './pages/GoFishPage';
import { CatchesPage } from './pages/CatchesPage';
import { FriendsPage } from './pages/FriendsPage';
import { ReviewFlow } from './pages/ReviewFlow';
import { AdminPage } from './pages/AdminPage';
import { PartnerPortal } from './pages/PartnerPortal';
import { BrandLogin } from './pages/BrandLogin';
import { BottomNav } from './components/BottomNav';
import { AppHeader } from './components/AppHeader';
import { Screen } from './types/navigation';
import { useStore } from './lib/store';
import { onDataChanged } from './lib/liveSync';
import { useScreenInit } from './useScreenInit.js';
export function App() {
  // Screens canvas: seed the initial screen from the active Screen preview
  // (?mp_screen=<id>), falling back to the normal 'landing' entry point.
  const screenInit = useScreenInit();
  const [currentScreen, setCurrentScreen] = useState<Screen>(screenInit?.currentScreen as Screen || 'landing');
  const {
    user,
    hydrateDrops,
    hydrateRecurringQuestions,
    hydratePartnerCodes,
    hydrateUserReservations
  } = useStore();
  const handleNavigate = (screen: Screen) => {
    setCurrentScreen(screen);
    window.scrollTo(0, 0);
  };
  // Keep drops, the recurring-user question config AND partner codes in sync
  // with the shared backend: pull the latest on load and poll every 15s so
  // anything an admin creates/edits/deletes on any device shows up here.
  useEffect(() => {
    const sync = () => {
      hydrateDrops();
      hydrateRecurringQuestions();
      hydratePartnerCodes();
      hydrateUserReservations();
    };
    sync();
    const interval = setInterval(sync, 15000);
    // Instant refresh when any surface (admin/brand/partner/user) changes data.
    const unsub = onDataChanged(() => sync());
    return () => {
      clearInterval(interval);
      unsub();
    };
  }, [hydrateDrops, hydrateRecurringQuestions, hydratePartnerCodes, hydrateUserReservations]);
  // Admin is reachable at the /admin path (e.g. gonefishingapp.com/admin) or
  // via the ?admin=true query fallback. The AdminPage itself is password-gated.
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, ''); // strip trailing /
    const isAdminPath = path === '/admin' || path.endsWith('/admin');
    const isPartnerPath = path === '/partner' || path.endsWith('/partner');
    const isBrandPath = path === '/brand' || path.endsWith('/brand');
    // /partner, /brand and /admin are all dark, password-gated staff/partner
    // tools — each fully separate from the (light) consumer app.
    if (isPartnerPath || window.location.search.includes('partner=true')) {
      setCurrentScreen('partner');
    } else if (isBrandPath || window.location.search.includes('brand=true')) {
      setCurrentScreen('brand');
    } else if (isAdminPath || window.location.search.includes('admin=true')) {
      setCurrentScreen('admin');
    }
  }, []);
  const handleRegisterSuccess = () => {
    // Changed: Go to Feed instead of ReviewFlow immediately
    // User will review later via Catches tab
    setCurrentScreen('feed');
  };
  // Returning members log in (no catch code) and land in the app, where the
  // bottom nav gives them review / reserve / profile.
  const handleLogin = () => {
    setCurrentScreen('feed');
  };
  const renderScreen = () => {
    switch (currentScreen) {
      case 'landing':
        return <LandingPage onRegisterSuccess={handleRegisterSuccess} onLogin={handleLogin} data-id="element-0" data-mp="1i3ulqmqla5ot" />;
      case 'feed':
        return <FeedPage onNavigate={handleNavigate} data-id="element-1" data-mp="1m3lakkwk2bqp" />;
      case 'events':
        return <EventsPage onNavigate={handleNavigate} data-id="element-2" data-mp="6ncmkevlxoyh" />;
      case 'go-fish':
        return <GoFishPage onNavigate={handleNavigate} data-id="element-3" data-mp="aqis1kawbc3w" />;
      case 'catches':
        return <CatchesPage onNavigate={handleNavigate} data-id="element-4" data-mp="qx5hi1qbouc2" />;
      case 'friends':
        return <FriendsPage onNavigate={handleNavigate} data-id="element-5" data-mp="qz8h9dha21tz" />;
      case 'review-flow':
        return <ReviewFlow onNavigate={handleNavigate} data-id="element-6" data-mp="m547ynkjhhpq" />;
      case 'admin':
        return <AdminPage data-id="element-7" data-mp="h8dpxup6cc1o" />;
      case 'partner':
        return <PartnerPortal data-id="element-8" data-mp="c89b093wzpdk" />;
      case 'brand':
        return <BrandLogin data-id="element-9" data-mp="c9kd84hpop2k" />;
      default:
        return <LandingPage onRegisterSuccess={handleRegisterSuccess} data-id="element-10" data-mp="c6ermz8ak5bl" />;
    }
  };
  const showHeaderAndNav = currentScreen !== 'admin' && currentScreen !== 'partner' && currentScreen !== 'brand' && currentScreen !== 'landing' && currentScreen !== 'review-flow';
  return <div className="min-h-screen bg-brand-light font-sans text-gray-900 flex flex-col" data-id="element-11" data-mp="zzwva86o826v">
      {showHeaderAndNav && <AppHeader data-id="element-12" data-mp="vyl1rrkoewte" />}
      <div className="flex-1 pb-16" data-id="element-13" data-mp="3knt14z9agroe">{renderScreen()}</div>
      {showHeaderAndNav && <BottomNav currentScreen={currentScreen} onNavigate={handleNavigate} data-id="element-14" data-mp="3gmh6gfbk5g9c" />}
    </div>;
}